/**
 * 经济数值（造价、B、目标骰次、星价、金币价、余额）的精确表示与显示。
 *
 * 这些数没有上限（leo 2026-10-16）：关卡一直往后加，造价到 1e24、B 到百万、目标骰次到 1e14 都是正常配置。
 * Number 只能精确表示到 2^53（约 9e15），再往上 `String(n)` 出 "1e+21"、末几位被悄悄舍掉——
 * 界面显示的不是库里的数，保存回去就把库里的数改了。所以：
 *
 * - **存**：`Money`。安全整数内照旧是 number（老数据、老用例、`===` 比较都不用动），超出就是十进制数字串。
 *   服务端把 DECIMAL 列按字符串下发，进门先过 `money()` 规范成这一种形状，两边相等才能用 `===` 判。
 *   不存 bigint：JSON.stringify 遇到 bigint 直接抛错，草稿自动保存、版本对比、协同合并都要序列化。
 * - **算**：一律 `toBig()` 转 bigint 再算，除法用 `divRound`（四舍五入，0.5 进位）。
 * - **显示**：明细格子用 `fmtAmount`（够大才缩写成「1.23亿 / 1.23B」），完整值放 `fmtFull` 进 title。
 */

export type Money = number | string;

const INT = /^-?\d+$/;
/** DECIMAL(x, y) 带小数位时服务端给 "123.00"；也接受科学计数法以外的普通小数串 */
const DEC = /^(-?)(\d+)\.(\d*)$/;

/**
 * 任意来源 → bigint；空、非数、非有限一律 null。
 * 小数按四舍五入取整（经济数值都是整数，小数只会来自 DECIMAL 的尾零或老数据）。
 * 超出 2^53 的 number 已经在别处丢过精度，这里只能原样接住，不会让它更糟。
 */
export function toBig(v: unknown): bigint | null {
  if (v == null || v === "") return null;
  if (typeof v === "bigint") return v;
  if (typeof v === "number") return Number.isFinite(v) ? BigInt(Math.round(v)) : null;
  if (typeof v !== "string") return null;
  const s = v.trim().replace(/^\+/, "");
  if (INT.test(s)) return BigInt(s);
  const m = DEC.exec(s);
  if (m) {
    const int = BigInt(m[2] || "0") + (m[3] && m[3][0] >= "5" ? 1n : 0n);
    return m[1] ? -int : int;
  }
  // "1e+21" 这类：只有它真是整数时才收，免得 "1.5e0" 被当成 2
  if (/^-?\d+(\.\d+)?e\+?\d+$/i.test(s)) {
    const n = Number(s);
    return Number.isSafeInteger(n) ? BigInt(n) : null;
  }
  return null;
}

const SAFE = BigInt(Number.MAX_SAFE_INTEGER);

/** bigint → 规范的 Money：安全整数内是 number，超出是数字串 */
export const fromBig = (b: bigint): Money => (b <= SAFE && b >= -SAFE ? Number(b) : b.toString());

/** 进门规范化：服务端给的 number / 数字串 / null 统一成 Money | null */
export const money = (v: unknown): Money | null => {
  const b = toBig(v);
  return b == null ? null : fromBig(b);
};

/** 近似值，只给比例、画图、蒙特卡洛模拟用；不能拿去存、显示金额或判等 */
export const approx = (v: unknown): number | null => {
  const b = toBig(v);
  return b == null ? null : Number(b);
};

export const bigAbs = (b: bigint) => (b < 0n ? -b : b);
export const bigMax = (a: bigint, b: bigint) => (a > b ? a : b);
export const bigMin = (a: bigint, b: bigint) => (a < b ? a : b);

/** 四舍五入的整除（0.5 远离 0）。d 必须非 0 */
export function divRound(n: bigint, d: bigint): bigint {
  if (d < 0n) { n = -n; d = -d; }
  const q = n / d, r = n % d;
  if (2n * bigAbs(r) >= d) return n < 0n ? q - 1n : q + 1n;
  return q;
}

/** 十进制位数（不含符号） */
export const digitsOf = (b: bigint) => bigAbs(b).toString().length;

/** log10(b)，b > 0；画对数坐标用。前 15 位有效数字足够定像素 */
export function log10Big(b: bigint): number {
  const s = bigAbs(b).toString();
  const head = s.slice(0, 15);
  return Math.log10(Number(head)) + (s.length - head.length);
}

/** 两个 bigint 之比，供偏差百分比用（取 18 位有效数字再转 number，足够 0.01% 精度） */
export function ratio(n: bigint, d: bigint): number {
  if (d === 0n) return Number.NaN;
  const shift = Math.max(0, Math.max(digitsOf(n), digitsOf(d)) - 15);
  const k = 10n ** BigInt(shift);
  return Number(n / k) / Number(d / k);
}

/**
 * 两位有效数字（与服务端 city-costs.ts roundPretty 同口径），在分数 n/d 上一次算完：
 * 100 以下四舍五入到整数；否则按位数取单位 10^(位数−2) 四舍五入。
 * 先在分数上定位数再除，避免「先除成整数、再取两位」的两次取整。
 */
export function roundPrettyFrac(n: bigint, d: bigint = 1n): bigint {
  if (d < 0n) { n = -n; d = -d; }
  if (n < 100n * d) return divRound(n, d);
  const unit = 10n ** BigInt(digitsOf(n / d) - 2);
  return divRound(n, d * unit) * unit;
}

/**
 * 算法总造价的取整（leo 2026-10-16）：≥100 四舍五入到 100 的整数倍（后两位为 0），<100 取整到个位。
 * 在分数 n/d 上一次取整，0.5 进位
 */
export function roundHundredFrac(n: bigint, d: bigint = 1n): bigint {
  if (d < 0n) { n = -n; d = -d; }
  if (n < 100n * d) return divRound(n, d);
  return divRound(n, d * 100n) * 100n;
}

/** 完整值，千分位分组：1,234,567,890,123,456,789,012。null → — */
export function fmtFull(v: unknown): string {
  const b = toBig(v);
  if (b == null) return "—";
  const s = bigAbs(b).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return b < 0n ? `-${s}` : s;
}

type CompactLang = "zh" | "en" | "ja";

/** 每档单位的十进制指数与写法。中日按万进（4 位一档），英文按千进（3 位一档） */
const UNITS: Record<CompactLang, [number, string][]> = {
  // 大陆习惯（leo 2026-10-16）：兆/京/垓 读者不认，到「亿亿」为止，再大转科学计数
  zh: [[4, "万"], [8, "亿"], [12, "万亿"], [16, "亿亿"]],
  ja: [[4, "万"], [8, "億"], [12, "兆"], [16, "京"], [20, "垓"], [24, "𥝱"], [28, "穣"], [32, "溝"], [36, "澗"], [40, "正"], [44, "載"]],
  en: [[3, "K"], [6, "M"], [9, "B"], [12, "T"], [15, "Qa"], [18, "Qi"], [21, "Sx"], [24, "Sp"], [27, "Oc"], [30, "No"], [33, "Dc"]],
};

/** b / 10^exp 保留 sig 位有效数字（四舍五入），去掉尾零："12.3"、"1.2"、"123" */
function scaled(b: bigint, exp: number, sig: number): { text: string; digits: number } {
  const intDigits = digitsOf(b) - exp; // 缩放后整数部分位数（≥1）
  const frac = Math.max(0, sig - intDigits);
  const v = divRound(b, 10n ** BigInt(exp - frac));
  let s = v.toString();
  if (frac > 0) {
    s = s.padStart(frac + 1, "0");
    s = `${s.slice(0, -frac)}.${s.slice(-frac)}`.replace(/\.?0+$/, "");
  }
  return { text: s, digits: s.split(".")[0].length };
}

/**
 * 缩写：|v| < 10^minDigits−1 原样千分位；否则 3 位有效数字 + 单位（1.23亿 / 12.3B）。
 * 单位表用完（中文过「载」、英文过 Dc）退到科学计数「1.23e48」——没有上限。
 */
export function fmtCompact(v: unknown, lang: CompactLang = "zh", { minDigits = 9, sig = 3 } = {}): string {
  const b = toBig(v);
  if (b == null) return "—";
  const a = bigAbs(b);
  const n = digitsOf(a);
  if (n < minDigits) return fmtFull(b);
  const sign = b < 0n ? "-" : "";
  const units = UNITS[lang];
  // 取不超过位数的最大一档；四舍五入进位后整数部分会多一位（9999万 → 1亿），进位了就升一档
  let i = units.length - 1;
  while (i >= 0 && units[i][0] > n - 1) i--;
  if (i >= 0) {
    let r = scaled(a, units[i][0], sig);
    // 最后一档的宽度按前一档算，超出就不再「1234载」地堆下去，交给科学计数
    const step = i + 1 < units.length ? units[i + 1][0] - units[i][0] : units[i][0] - (units[i - 1]?.[0] ?? 0);
    if (r.digits > step && i + 1 < units.length) { i++; r = scaled(a, units[i][0], sig); }
    if (r.digits <= step) return `${sign}${r.text}${units[i][1]}`;
  }
  // 科学计数：从数字串拼，不经 Number（超过 1e308 也照样写得出来）
  const m = scaled(a, n - 1, sig);
  const exp = m.digits > 1 ? n : n - 1; // 9.995e40 进位成 10 → 1e41
  const mant = m.digits > 1 ? scaled(a, n, sig).text : m.text;
  return `${sign}${mant}e${exp}`;
}

/** 明细格子里的金额：大到放不下才缩写。界面语言由调用方传（useI18n().lang） */
export const fmtAmount = (v: unknown, lang: CompactLang = "zh") => fmtCompact(v, lang);

/** bigint × 小数个数（奖励以 B 计的数量可以带小数，如 0.5B）：数量按千分精度折整数再乘，四舍五入 */
export function mulQty(b: bigint, qty: number): bigint {
  if (Number.isInteger(qty)) return b * BigInt(qty);
  return divRound(b * BigInt(Math.round(qty * 1000)), 1000n);
}

/**
 * 输入框精确整数模式（Field.tsx 的 `exact`）的草稿 → 值：草稿只含数字（键盘层已挡掉 - 和 .）。
 * 不经 Number——Number("1000000000000000000001") 会变成 1e21、末位丢掉。
 * 安全整数内仍给 number，超出给数字串。中间态空串 = 没有值。
 */
export const parseExactDraft = (raw: string): Money | null => (raw === "" ? null : fromBig(BigInt(raw)));

/** 精确模式下「草稿是否还代表当前值」：按 bigint 比，"007" 与 7 相等 */
export const sameExact = (draft: string, value: unknown): boolean => {
  const v = toBig(value);
  return draft === "" ? v == null : v != null && BigInt(draft) === v;
};

/** 精确模式下聚焦编辑时显示的裸数字串（不经 String(number)，大数不会变成 "1e+21"） */
export const exactText = (v: unknown): string => toBig(v)?.toString() ?? "";

/** bigint 求和；有一项是 null 就整体 null（与 gridSum 同一个原则：半截的合计比没有更危险） */
export function sumBig(xs: readonly (bigint | null | undefined)[]): bigint | null {
  let s = 0n;
  for (const x of xs) {
    if (x == null) return null;
    s += x;
  }
  return s;
}

/** bigint 求和，跳过 null */
export const sumBigPartial = (xs: readonly (bigint | null | undefined)[]): bigint =>
  xs.reduce<bigint>((a, x) => (x == null ? a : a + x), 0n);
